import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, MessageSquare } from 'lucide-react';
import Button from '../ui/Button';
import type { Mentor } from '../../lib/types';

interface SessionRequestModalProps {
  mentor: Mentor;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    mentorId: string;
    scheduledAt: string;
    notes: string;
  }) => Promise<void>;
}

const SessionRequestModal: React.FC<SessionRequestModalProps> = ({
  mentor,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const scheduledAt = new Date(`${date}T${time}`).toISOString();
      await onSubmit({
        mentorId: mentor.id,
        scheduledAt,
        notes,
      });
      onClose();
    } catch (error) {
      console.error('Error submitting session request:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Improved backdrop with blur effect */}
            <div
              className="fixed inset-0 bg-gray-900 bg-opacity-75 backdrop-blur-sm transition-opacity"
              onClick={onClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="inline-block w-full max-w-lg transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all sm:my-8"
            >
              {/* Header */}
              <div className="bg-primary-900 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">
                    Request Mentoring Session
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-1 text-primary-100">
                  Schedule a 15-minute session with {mentor.name}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                {/* Mentor Info */}
                <div className="mb-6 p-4 bg-primary-50 rounded-xl">
                  <div className="flex items-center">
                    <img
                      src={mentor.avatar_url}
                      alt={mentor.name}
                      className="h-12 w-12 rounded-full object-cover border-2 border-primary-200"
                    />
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900">{mentor.name}</h4>
                      <p className="text-sm text-gray-600">{mentor.title} at {mentor.company}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Date Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Date
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        required
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="input pl-10 w-full"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  {/* Time Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Time
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="time"
                        required
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        className="input pl-10 w-full"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes for Mentor
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 text-gray-400" />
                      <textarea
                        required
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="input pl-10 h-32 resize-none"
                        placeholder="Briefly describe what you'd like to discuss..."
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-8 flex justify-end gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={loading}
                    className="bg-primary-600 hover:bg-primary-700"
                  >
                    Request Session
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SessionRequestModal;